/**
 * CEO Command Center - AI Tool Executor
 *
 * Executes AI tool calls and interacts with the data store.
 *
 * @module services/ai/executor
 * @version 11.0.0
 */

import { getToolByName } from './tools';
import type { AITool } from './tools';

// ============================================
// TYPES
// ============================================

export interface ToolExecutionResult {
  success: boolean;
  message: string;
  data?: unknown;
  requiresConfirmation?: boolean;
  pendingAction?: PendingAction;
}

export interface PendingAction {
  toolName: string;
  params: Record<string, unknown>;
  confirmationMessage: string;
}

export interface DataStore {
  // Cash flow
  cashflow: Array<{
    id: string;
    month: string;
    monthDate: string;
    recurringRevenue: number;
    newDealRevenue: number;
    personnelCost: number;
    infrastructureCost: number;
    marketingCost: number;
    legalCost: number;
    otherCost: number;
    balance?: number;
    isForecast?: boolean;
  }>;
  updateCashFlowField: (month: string, field: string, value: number) => Promise<void>;
  createCashFlowRecord: (record: Record<string, unknown>) => Promise<void>;

  // Deals
  deals: Array<{
    id: string;
    prospect: string;
    tcv: number;
    probability: number;
    stage: string;
    owner: string;
    closeDate: string | null;
  }>;
  createDeal: (deal: Record<string, unknown>) => Promise<unknown>;
  updateDeal: (id: string, updates: Record<string, unknown>) => Promise<unknown>;
  deleteDeal: (id: string) => Promise<void>;

  // Tasks
  tasks: Array<{
    id: string;
    title: string;
    domain: string;
    owner: string;
    status: string;
    priority: string;
    isSignal: boolean;
  }>;
  createTask: (task: Record<string, unknown>) => Promise<unknown>;
  updateTask: (id: string, updates: Record<string, unknown>) => Promise<unknown>;

  // Clients
  clients: Array<{
    id: string;
    name: string;
    acv2026?: number;
    health: string;
    owner: string;
  }>;
  createClient: (client: Record<string, unknown>) => Promise<unknown>;
  updateClient: (id: string, updates: Record<string, unknown>) => Promise<unknown>;

  // Investors
  investors: Array<{
    id: string;
    name: string;
    firm?: string;
    stage: string;
    amount?: number;
  }>;
  addInvestor: (investor: Record<string, unknown>) => Promise<void>;
  updateInvestor: (id: string, updates: Record<string, unknown>) => Promise<void>;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Format currency for display
 */
const formatEUR = (value: number): string => {
  return new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Find entity by name (fuzzy match)
 */
const findByName = <T extends { name?: string; prospect?: string; title?: string }>(
  items: T[],
  searchName: string
): T | undefined => {
  const search = searchName.toLowerCase().trim();
  return items.find(item => {
    const name = (item.name || item.prospect || item.title || '').toLowerCase();
    return name === search || name.includes(search) || search.includes(name);
  });
};

/**
 * Find cash flow record by month
 */
const findCashFlowByMonth = (
  cashflow: DataStore['cashflow'],
  month: string
): DataStore['cashflow'][0] | undefined => {
  const search = month.toLowerCase().trim();
  return cashflow.find(cf => cf.month.toLowerCase() === search);
};

// ============================================
// TOOL EXECUTORS
// ============================================

/**
 * Execute a finance tool
 */
async function executeFinanceTool(
  toolName: string,
  params: Record<string, unknown>,
  store: DataStore
): Promise<ToolExecutionResult> {
  switch (toolName) {
    case 'update_cash_position': {
      const { month, balance } = params as { month: string; balance: number };
      const record = findCashFlowByMonth(store.cashflow, month);

      if (!record) {
        return {
          success: false,
          message: `No cash flow record found for ${month}. Available months: ${store.cashflow.slice(-6).map(cf => cf.month).join(', ')}`,
        };
      }

      await store.updateCashFlowField(month, 'balance', balance);
      return {
        success: true,
        message: `Updated ${month} cash position to ${formatEUR(balance)}`,
        data: { month, balance },
      };
    }

    case 'update_cashflow_revenue': {
      const { month, field, value } = params as { month: string; field: string; value: number };
      const record = findCashFlowByMonth(store.cashflow, month);

      if (!record) {
        return {
          success: false,
          message: `No cash flow record found for ${month}`,
        };
      }

      await store.updateCashFlowField(month, field, value);
      const fieldLabel = field === 'recurringRevenue' ? 'recurring revenue' : 'new deal revenue';
      return {
        success: true,
        message: `Updated ${month} ${fieldLabel} to ${formatEUR(value)}`,
        data: { month, field, value },
      };
    }

    case 'update_cashflow_expense': {
      const { month, field, value } = params as { month: string; field: string; value: number };
      const record = findCashFlowByMonth(store.cashflow, month);

      if (!record) {
        return {
          success: false,
          message: `No cash flow record found for ${month}`,
        };
      }

      await store.updateCashFlowField(month, field, value);
      const fieldLabels: Record<string, string> = {
        personnelCost: 'personnel cost',
        infrastructureCost: 'infrastructure cost',
        marketingCost: 'marketing cost',
        legalCost: 'legal cost',
        otherCost: 'other cost',
      };
      return {
        success: true,
        message: `Updated ${month} ${fieldLabels[field] || field} to ${formatEUR(value)}`,
        data: { month, field, value },
      };
    }

    case 'create_cashflow_record': {
      const { month, ...fields } = params as { month: string } & Record<string, unknown>;
      const existing = findCashFlowByMonth(store.cashflow, month);

      if (existing) {
        return {
          success: false,
          message: `Cash flow record for ${month} already exists. Use update commands instead.`,
        };
      }

      await store.createCashFlowRecord({ month, ...fields });
      return {
        success: true,
        message: `Created cash flow record for ${month}`,
        data: { month, ...fields },
      };
    }

    default:
      return { success: false, message: `Unknown finance tool: ${toolName}` };
  }
}

/**
 * Execute a sales tool
 */
async function executeSalesTool(
  toolName: string,
  params: Record<string, unknown>,
  store: DataStore
): Promise<ToolExecutionResult> {
  switch (toolName) {
    case 'create_deal': {
      const { prospect, tcv, probability, stage, owner, closeDate } = params as {
        prospect: string;
        tcv: number;
        probability?: number;
        stage?: string;
        owner?: string;
        closeDate?: string;
      };

      // Check if deal already exists
      const existing = findByName(store.deals, prospect);
      if (existing) {
        return {
          success: false,
          message: `Deal with ${prospect} already exists. Use update_deal to modify it.`,
        };
      }

      const deal = await store.createDeal({
        prospect,
        tcv,
        probability: probability ? (probability > 1 ? probability / 100 : probability) : 0.1,
        stage: stage || 'Qualification',
        owner: owner || '',
        closeDate: closeDate || null,
      });

      return {
        success: true,
        message: `Created deal: ${prospect} with TCV ${formatEUR(tcv)}`,
        data: deal,
      };
    }

    case 'update_deal': {
      const { prospect, ...updates } = params as { prospect: string } & Record<string, unknown>;
      const deal = findByName(store.deals, prospect);

      if (!deal) {
        return {
          success: false,
          message: `No deal found matching "${prospect}". Available deals: ${store.deals.slice(0, 5).map(d => d.prospect).join(', ')}`,
        };
      }

      // Normalize probability
      if (updates.probability && (updates.probability as number) > 1) {
        updates.probability = (updates.probability as number) / 100;
      }

      await store.updateDeal(deal.id, updates);
      const updateList = Object.entries(updates)
        .map(([k, v]) => `${k}: ${typeof v === 'number' && k.includes('cv') ? formatEUR(v) : v}`)
        .join(', ');

      return {
        success: true,
        message: `Updated ${prospect}: ${updateList}`,
        data: { id: deal.id, ...updates },
      };
    }

    case 'delete_deal': {
      const { prospect } = params as { prospect: string };
      const deal = findByName(store.deals, prospect);

      if (!deal) {
        return {
          success: false,
          message: `No deal found matching "${prospect}"`,
        };
      }

      await store.deleteDeal(deal.id);
      return {
        success: true,
        message: `Deleted deal: ${prospect}`,
        data: { id: deal.id, prospect },
      };
    }

    default:
      return { success: false, message: `Unknown sales tool: ${toolName}` };
  }
}

/**
 * Execute a task tool
 */
async function executeTaskTool(
  toolName: string,
  params: Record<string, unknown>,
  store: DataStore
): Promise<ToolExecutionResult> {
  switch (toolName) {
    case 'create_task': {
      const { title, domain, owner, priority, targetDate, isSignal } = params as {
        title: string;
        domain?: string;
        owner?: string;
        priority?: string;
        targetDate?: string;
        isSignal?: boolean;
      };

      const task = await store.createTask({
        title,
        domain: domain || 'EMG',
        owner: owner || '',
        priority: priority || 'Medium',
        status: 'Not Started',
        targetDate: targetDate || null,
        isSignal: isSignal || false,
      });

      return {
        success: true,
        message: `Created task: "${title}"${isSignal ? ' (marked as signal)' : ''}`,
        data: task,
      };
    }

    case 'update_task': {
      const { taskId, ...updates } = params as { taskId: string } & Record<string, unknown>;

      // Find task by ID or title
      const task = store.tasks.find(t => t.id === taskId) || findByName(store.tasks, taskId);

      if (!task) {
        return {
          success: false,
          message: `No task found matching "${taskId}"`,
        };
      }

      await store.updateTask(task.id, updates);
      const updateList = Object.entries(updates).map(([k, v]) => `${k}: ${v}`).join(', ');

      return {
        success: true,
        message: `Updated task "${task.title}": ${updateList}`,
        data: { id: task.id, ...updates },
      };
    }

    case 'complete_task': {
      const { taskId } = params as { taskId: string };
      const task = store.tasks.find(t => t.id === taskId) || findByName(store.tasks, taskId);

      if (!task) {
        return {
          success: false,
          message: `No task found matching "${taskId}"`,
        };
      }

      await store.updateTask(task.id, { status: 'Done' });
      return {
        success: true,
        message: `Completed task: "${task.title}"`,
        data: { id: task.id, status: 'Done' },
      };
    }

    default:
      return { success: false, message: `Unknown task tool: ${toolName}` };
  }
}

/**
 * Execute a client tool
 */
async function executeClientTool(
  toolName: string,
  params: Record<string, unknown>,
  store: DataStore
): Promise<ToolExecutionResult> {
  switch (toolName) {
    case 'create_client': {
      const { name, acv2026, health, owner, industry } = params as {
        name: string;
        acv2026?: number;
        health?: string;
        owner?: string;
        industry?: string;
      };

      const existing = findByName(store.clients, name);
      if (existing) {
        return {
          success: false,
          message: `Client "${name}" already exists. Use update_client to modify.`,
        };
      }

      const client = await store.createClient({
        name,
        acv2026: acv2026 || 0,
        health: health || 'Healthy',
        owner: owner || '',
        industry: industry || '',
      });

      return {
        success: true,
        message: `Created client: ${name}${acv2026 ? ` with ACV ${formatEUR(acv2026)}` : ''}`,
        data: client,
      };
    }

    case 'update_client': {
      const { name, ...updates } = params as { name: string } & Record<string, unknown>;
      const client = findByName(store.clients, name);

      if (!client) {
        return {
          success: false,
          message: `No client found matching "${name}"`,
        };
      }

      await store.updateClient(client.id, updates);
      const updateList = Object.entries(updates)
        .map(([k, v]) => `${k}: ${typeof v === 'number' ? formatEUR(v) : v}`)
        .join(', ');

      return {
        success: true,
        message: `Updated ${name}: ${updateList}`,
        data: { id: client.id, ...updates },
      };
    }

    default:
      return { success: false, message: `Unknown client tool: ${toolName}` };
  }
}

/**
 * Execute an investor tool
 */
async function executeInvestorTool(
  toolName: string,
  params: Record<string, unknown>,
  store: DataStore
): Promise<ToolExecutionResult> {
  switch (toolName) {
    case 'create_investor': {
      const { name, firm, stage, amount, email } = params as {
        name: string;
        firm?: string;
        stage?: string;
        amount?: number;
        email?: string;
      };

      const existing = findByName(store.investors, name);
      if (existing) {
        return {
          success: false,
          message: `Investor "${name}" already exists. Use update_investor to modify.`,
        };
      }

      await store.addInvestor({
        name,
        firm: firm || '',
        stage: stage || 'Research',
        amount: amount || 0,
        email: email || '',
      });

      return {
        success: true,
        message: `Added investor: ${name}${firm ? ` (${firm})` : ''}${amount ? ` - ${formatEUR(amount)}` : ''}`,
        data: { name, firm, stage, amount },
      };
    }

    case 'update_investor': {
      const { name, ...updates } = params as { name: string } & Record<string, unknown>;
      const investor = findByName(store.investors, name);

      if (!investor) {
        return {
          success: false,
          message: `No investor found matching "${name}"`,
        };
      }

      await store.updateInvestor(investor.id, updates);
      const updateList = Object.entries(updates)
        .map(([k, v]) => `${k}: ${typeof v === 'number' ? formatEUR(v) : v}`)
        .join(', ');

      return {
        success: true,
        message: `Updated ${name}: ${updateList}`,
        data: { id: investor.id, ...updates },
      };
    }

    default:
      return { success: false, message: `Unknown investor tool: ${toolName}` };
  }
}

// ============================================
// MAIN EXECUTOR
// ============================================

/**
 * Execute an AI tool call
 */
export async function executeAITool(
  toolName: string,
  params: Record<string, unknown>,
  store: DataStore
): Promise<ToolExecutionResult> {
  const tool = getToolByName(toolName);

  if (!tool) {
    return {
      success: false,
      message: `Unknown tool: ${toolName}`,
    };
  }

  try {
    switch (tool.category) {
      case 'finance':
        return await executeFinanceTool(toolName, params, store);
      case 'sales':
        return await executeSalesTool(toolName, params, store);
      case 'tasks':
        return await executeTaskTool(toolName, params, store);
      case 'clients':
        return await executeClientTool(toolName, params, store);
      case 'investors':
        return await executeInvestorTool(toolName, params, store);
      default:
        return { success: false, message: `Unknown tool category: ${tool.category}` };
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error occurred';
    console.error(`Tool execution error (${toolName}):`, error);
    return {
      success: false,
      message: `Failed to execute ${toolName}: ${message}`,
    };
  }
}

/**
 * Check if a tool requires confirmation before execution
 */
export function toolRequiresConfirmation(toolName: string): boolean {
  const tool = getToolByName(toolName);
  return tool?.requiresConfirmation ?? true;
}

/**
 * Create a pending action for confirmation
 */
export function createPendingAction(
  toolName: string,
  params: Record<string, unknown>
): PendingAction {
  const tool = getToolByName(toolName);
  let confirmationMessage = `Execute ${toolName}?`;

  // Create human-readable confirmation message
  switch (toolName) {
    case 'update_cash_position':
      confirmationMessage = `Update ${params.month} cash position to ${formatEUR(params.balance as number)}?`;
      break;
    case 'update_cashflow_revenue':
    case 'update_cashflow_expense':
      confirmationMessage = `Update ${params.month} ${params.field} to ${formatEUR(params.value as number)}?`;
      break;
    case 'create_deal':
      confirmationMessage = `Create deal with ${params.prospect} (TCV: ${formatEUR(params.tcv as number)})?`;
      break;
    case 'update_deal':
      confirmationMessage = `Update deal ${params.prospect}?`;
      break;
    case 'delete_deal':
      confirmationMessage = `Delete deal ${params.prospect}? This cannot be undone.`;
      break;
    case 'create_client':
      confirmationMessage = `Add client ${params.name}?`;
      break;
    case 'update_client':
      confirmationMessage = `Update client ${params.name}?`;
      break;
    case 'create_investor':
      confirmationMessage = `Add investor ${params.name}?`;
      break;
    case 'update_investor':
      confirmationMessage = `Update investor ${params.name}?`;
      break;
  }

  return {
    toolName,
    params,
    confirmationMessage,
  };
}
